import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Plus, X, CheckCircle, Clock } from '@phosphor-icons/react';

/**
 * Co-Sanctioning Organizations panel for ShowEdit (v217).
 *
 * A producer can propose that another org co-sanction their show. The
 * request lands in the target org's pending queue on their Points admin
 * page; on approval, the show starts counting toward that org's
 * standings using ITS point table + ITS member roster.
 *
 * This component is a self-contained panel that reads/writes only:
 *   GET/POST/DELETE /api/shows/{show_id}/co-sanctioning
 *   GET /api/organizations/public   (dropdown source)
 */
export const CoSanctioningSection = ({ showId, ownerOrgId }) => {
  const [approved, setApproved] = useState([]);
  const [pending, setPending] = useState([]);
  const [allOrgs, setAllOrgs] = useState([]);
  const [selected, setSelected] = useState('');
  const [filterQ, setFilterQ] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [state, orgs] = await Promise.all([
        api.get(`/shows/${showId}/co-sanctioning`),
        api.get(`/organizations/public`),
      ]);
      setApproved(state.data.approved || []);
      setPending(state.data.pending || []);
      setAllOrgs(orgs.data || []);
    } catch { /* silent — panel is optional */ }
  }, [showId]);

  useEffect(() => { load(); }, [load]);

  const propose = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/shows/${showId}/co-sanctioning`, { organization_id: selected });
      const status = r.data.status;
      if (status === 'pending') toast.success('Request sent — awaiting the org admin\'s approval.');
      else if (status === 'already_approved') toast.info('Already an approved co-sanction.');
      else if (status === 'already_pending') toast.info('Request already pending approval.');
      setSelected('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally { setBusy(false); }
  };

  const remove = async (orgId) => {
    if (!window.confirm('Remove this co-sanction?')) return;
    try {
      await api.delete(`/shows/${showId}/co-sanctioning/${orgId}`);
      load();
    } catch { toast.error('Remove failed'); }
  };

  const linkedIds = new Set([
    ownerOrgId,
    ...approved.map((x) => x.organization_id),
    ...pending.map((x) => x.organization_id),
  ]);
  const q = filterQ.trim().toLowerCase();
  const available = allOrgs
    .filter((o) => !linkedIds.has(o.id))
    .filter((o) => !q || (o.name || '').toLowerCase().includes(q));

  return (
    <div className="space-y-4" data-testid="co-sanctioning-section">
      <p className="text-[11px] text-[#9E9E9E]">
        Propose that other organizations co-sanction this show. On approval by that org&apos;s
        admin, the show will count toward their season point standings alongside yours.
        Riders who are members of both orgs earn points in both.
      </p>

      {/* Approved list */}
      {approved.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-[#34C759] mb-1.5 flex items-center gap-1.5">
            <CheckCircle size={13} weight="fill" /> Approved
          </div>
          <div className="flex flex-wrap gap-2">
            {approved.map((o) => (
              <span key={o.organization_id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30 rounded text-xs" data-testid={`co-sanction-approved-${o.organization_id}`}>
                {o.name}
                <button type="button" onClick={() => remove(o.organization_id)} className="hover:text-[#FF3B30]" data-testid={`co-sanction-remove-${o.organization_id}`}>
                  <X size={11} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Pending list */}
      {pending.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-[#FF9500] mb-1.5 flex items-center gap-1.5">
            <Clock size={13} weight="fill" /> Pending Approval
          </div>
          <div className="flex flex-wrap gap-2">
            {pending.map((o) => (
              <span key={o.organization_id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FF9500]/10 text-[#FF9500] border border-[#FF9500]/30 rounded text-xs" data-testid={`co-sanction-pending-${o.organization_id}`}>
                {o.name}
                <button type="button" onClick={() => remove(o.organization_id)} className="hover:text-[#FF3B30]" data-testid={`co-sanction-cancel-${o.organization_id}`}>
                  <X size={11} weight="bold" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {approved.length === 0 && pending.length === 0 && (
        <p className="text-[#555] text-xs italic">No co-sanctioning organizations yet.</p>
      )}

      {/* Add new */}
      <div className="flex flex-col gap-2 pt-2 border-t border-[#262626]">
        <input
          type="text"
          value={filterQ}
          onChange={(e) => setFilterQ(e.target.value)}
          placeholder="Filter orgs..."
          className="w-full sm:w-64 px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm text-white placeholder-[#555] focus:border-[#0096FF]/50 focus:outline-none"
          data-testid="co-sanction-org-filter"
        />
        <div className="flex items-center gap-2">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="flex-1 px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm text-white"
            data-testid="co-sanction-org-select"
          >
            <option value="">— Select an organization —</option>
            {available.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={propose}
            disabled={!selected || busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30 rounded text-xs font-bold hover:bg-[#0096FF]/25 disabled:opacity-40"
            data-testid="co-sanction-propose-btn"
          >
            <Plus size={12} weight="bold" /> Request
          </button>
        </div>
      </div>
    </div>
  );
};
